// Approved quick-order flows and cash/manual-card recording. Retain identity after ACK loss;
// store opaque IDs, never credentials or money payloads. Cloud lane-busy alone
// may resend ONCE with the exact already-sent key; key creation never runs on replay.
import axios from 'axios';
import { durableRequest, formatDurableError, retainedRequestKey } from './durableErrors';

const runtimeByOrigin = new Map();
// Same-origin deployments (the VPS) build with an empty REACT_APP_BACKEND_URL; `new URL(path, '')` throws.
const backendBase = () => process.env.REACT_APP_BACKEND_URL || window.location.origin;
const configURL = config => new URL(config.url, config.baseURL || backendBase());

const canonical = value => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
  }
  return value;
};

const actorIdentity = headers => {
  const bearer = headers?.Authorization || headers?.get?.('Authorization') || '';
  try {
    const encoded = bearer.replace(/^Bearer\s+/i, '').split('.')[1];
    const claims = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/')));
    // Storage namespace only; the server still verifies the token and tenant.
    return [claims.club_id, claims.user_id || claims.id];
  } catch {
    throw new Error('A valid session is required before recording this operation.');
  }
};

axios.interceptors.request.use(async config => {
  if (config._rentosLaneBusyRetried) {
    if (!config._rentosLaneRetryKey || retainedRequestKey(config) !== config._rentosLaneRetryKey) {
      throw new Error('Үйлдлийн таних түлхүүр өөрчлөгдсөн тул дахин илгээгээгүй.');
    }
    return config;
  }
  if (!['post', 'delete'].includes(config.method?.toLowerCase())) return config;
  const url = new URL(config.url, backendBase());
  const stock = /^\/api\/quick-orders\/[^/]+\/items$/.test(url.pathname);
  const pay = /^\/api\/quick-orders\/[^/]+\/pay$/.test(url.pathname);
  const sessionPay = /^\/api\/sessions\/[^/]+\/pay$/.test(url.pathname);
  const movement = /^\/api\/(?:orders\/[^/]+(?:\/return)?|quick-orders\/[^/]+\/items\/[^/]+|billing\/credits\/[^/]+\/repay|members\/[^/]+\/repay|products\/[^/]+\/adjust-stock|inventory\/(?:import(?:\/bulk)?|movement|count))$/.test(url.pathname);
  if (!stock && !pay && !sessionPay && !movement) return config;
  let body = config.data;
  if (typeof body === 'string') body = JSON.parse(body);
  const credit = pay && body?.payment_method === 'credit';
  const recorded = movement || (sessionPay && ['cash', 'card'].includes(body?.payment_method ?? 'cash'));
  const suppliedKey = config.headers?.['Idempotency-Key'] || config.headers?.get?.('Idempotency-Key');
  if ((!stock && !credit && !recorded) || (!recorded && body?.operation_key) || (recorded && suppliedKey)) return config;
  const identity = JSON.stringify(canonical([actorIdentity(config.headers), url.origin, url.pathname, body]));
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity));
  const storageKey = `rentos:durable:${Array.from(new Uint8Array(hash), x => x.toString(16).padStart(2, '0')).join('')}`;
  const operationKey = localStorage.getItem(storageKey) || crypto.randomUUID();
  // Storage failure must prevent sending an unrecoverable anonymous request.
  localStorage.setItem(storageKey, operationKey);
  if (recorded) {
    config.headers = config.headers || {};
    config.headers['Idempotency-Key'] = operationKey;
  } else {
    config.data = { ...body, operation_key: operationKey };
  }
  config.durableIntent = { storageKey, operationKey };
  return config;
});

const acknowledge = config => {
  const saved = config?.durableIntent;
  if (saved && localStorage.getItem(saved.storageKey) === saved.operationKey) {
    localStorage.removeItem(saved.storageKey);
  }
};

axios.interceptors.response.use(response => {
  if (response.config?.url) {
    const url = configURL(response.config);
    if (url.pathname === '/api/pos/mode') {
      runtimeByOrigin.set(url.origin, response.data?.local_pos === true ? 'local_pos' : response.data?.mode);
    }
  }
  if (response.data?.success === true && response.data?.operation_id) acknowledge(response.config);
  return response;
}, async error => {
  // A generic 4xx (including an auth failure after a lost success ACK) does NOT
  // prove no effects happened. Only the persisted REJECTED root can prove that.
  const detail = error.response?.data?.detail;
  const config = error.config;
  const url = config?.url ? configURL(config) : null;
  if (url?.pathname === '/api/pos/mode') runtimeByOrigin.delete(url.origin);
  const cloudOperation = url && url.origin === new URL(backendBase()).origin &&
    runtimeByOrigin.get(url.origin) === 'cloud' && durableRequest(config, url.pathname);
  const key = retainedRequestKey(config);
  if (cloudOperation && error.response?.status === 409 && detail?.code === 'operation_lane_busy' &&
      !config._rentosLaneBusyRetried && typeof key === 'string' && key.trim().length >= 8) {
    config._rentosLaneBusyRetried = true;
    config._rentosLaneRetryKey = key;
    return axios.request(config);
  }
  if (detail?.operation_state === 'REJECTED' && detail?.operation_id &&
      detail.request_key === error.config?.durableIntent?.operationKey) acknowledge(error.config);
  if (detail && typeof detail === 'object' && (detail.message || (cloudOperation && detail.code))) {
    error.durableCode = detail.code;
    error.durableDetail = detail;
    error.durableMessage = cloudOperation ? formatDurableError(detail.code) : null;
    error.response.data.detail = error.durableMessage || detail.message;
  }
  return Promise.reject(error);
});